import { useEffect, useState } from "react";
import { Cake, Gift, Phone, Mail, MessageCircle, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { popoverVariants } from "../../../utils/framerMotion";
import { useAppDispatch, useAppSelector } from "../../../import";
import { getAgentClientBirthdays } from "../../../store/agent/dashboard-slice";
import { usePopover } from "../../../hooks/usePopover";
import { toWhatsAppNumber } from "../../../utils/helpers/common";

const initials = (f?: string, s?: string) =>
  `${(f?.[0] ?? "").toUpperCase()}${(s?.[0] ?? "").toUpperCase()}` || "?";

const BirthdayBell = () => {
  const dispatch = useAppDispatch();
  const { birthdayClients, isBirthdaysLoading } = useAppSelector(
    (state) => state.agentDashboard,
  );
  const [open, setOpen] = useState(false);
  const ref = usePopover<HTMLDivElement>({ isOpen: open, onClose: () => setOpen(false) });

  useEffect(() => {
    dispatch(getAgentClientBirthdays());
  }, [dispatch]);

  const count = birthdayClients.length;

  const animate = count > 0 && !open;

  return (
    <div className="relative" ref={ref}>
      <style>{`
        @keyframes bday-wiggle {
          0%, 55%, 100% { transform: rotate(0deg) scale(1); }
          58% { transform: rotate(-16deg) scale(1.08); }
          62% { transform: rotate(13deg) scale(1.08); }
          66% { transform: rotate(-10deg) scale(1.05); }
          70% { transform: rotate(7deg) scale(1.03); }
          74% { transform: rotate(-4deg) scale(1.01); }
          78% { transform: rotate(0deg) scale(1); }
        }
        @keyframes bday-badge-pop {
          0%, 60%, 100% { transform: scale(1); }
          65% { transform: scale(1.25); }
          72% { transform: scale(0.92); }
          80% { transform: scale(1); }
        }
        .bday-gift-anim { animation: bday-wiggle 2.6s ease-in-out infinite; transform-origin: 50% 15%; }
        .bday-badge-anim { animation: bday-badge-pop 2.6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .bday-gift-anim, .bday-badge-anim { animation: none; }
        }
      `}</style>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Client birthdays today"
        aria-label="Client birthdays today"
        className={`relative rounded-lg border px-2.5 py-1.5 transition-colors ${
          count > 0
            ? "border-[#B87F4B]/45 text-[#D19A66] hover:bg-[#B87F4B]/15"
            : "border-white/12 text-[#ADA79A] hover:bg-white/5 hover:text-[#F1EDE4]"
        }`}
      >
        <Gift size={18} className={animate ? "bday-gift-anim" : ""} />
        {count > 0 && (
          <span
            className={`absolute -top-1.5 -right-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#B87F4B] px-1 text-[10px] font-bold leading-none text-[#1C1006] ${
              animate ? "bday-badge-anim" : ""
            }`}
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      <AnimatePresence>
      {open && (
        <motion.div variants={popoverVariants} initial="hidden" animate="visible" exit="exit" className="absolute right-0 z-50 mt-2 w-80 origin-top-right overflow-hidden rounded-xl border border-[#2A2622] bg-[#141210] shadow-[0_16px_48px_-12px_rgba(0,0,0,0.55)]">
          <div className="flex items-center justify-between border-b border-[#2A2622] px-4 py-3">
            <div className="flex items-center gap-2">
              <Cake size={15} className="text-[#D19A66]" />
              <span className="text-sm font-semibold text-[#F1EDE4]">
                Birthdays today
              </span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-[#8A857A] hover:text-[#F1EDE4]"
              aria-label="Close"
            >
              <X size={15} />
            </button>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {isBirthdaysLoading ? (
              <p className="px-4 py-6 text-center text-xs text-[#8A857A]">
                Loading…
              </p>
            ) : count === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-[#8A857A]">
                None of your clients have a birthday today.
              </p>
            ) : (
              <ul className="divide-y divide-[#2A2622]">
                {birthdayClients.map((c) => {
                  const digits = toWhatsAppNumber(c.phoneNumber);
                  const name = `${c.firstName ?? ""} ${c.surname ?? ""}`.trim();
                  const wish = `Happy Birthday, ${c.firstName || ""}! 🎉`;
                  return (
                    <li key={c._id} className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#B87F4B]/30 bg-[#2A1A0E] text-xs font-semibold text-[#D19A66]">
                          {c.imageUrl ? (
                            <img
                              src={c.imageUrl}
                              alt={name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            initials(c.firstName, c.surname)
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-[#F1EDE4]">
                            {name || "—"}
                          </p>
                          <p className="truncate text-[11px] text-[#8A857A]">
                            {c.accountId}
                            {c.turningAge ? ` · turns ${c.turningAge}` : ""}
                          </p>
                        </div>
                      </div>
                      {(digits || c.email) && (
                        <div className="mt-2 flex items-center gap-1.5 pl-12">
                          {digits && (
                            <a
                              href={`tel:${c.phoneNumber}`}
                              title="Call"
                              className="rounded-md border border-[#2A2622] p-1.5 text-[#ADA79A] hover:border-[#B87F4B]/40 hover:text-[#D19A66]"
                            >
                              <Phone size={13} />
                            </a>
                          )}
                          {digits && (
                            <a
                              href={`https://wa.me/${digits}?text=${encodeURIComponent(wish)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="WhatsApp"
                              className="rounded-md border border-[#2A2622] p-1.5 text-[#ADA79A] hover:border-[#B87F4B]/40 hover:text-[#D19A66]"
                            >
                              <MessageCircle size={13} />
                            </a>
                          )}
                          {c.email && (
                            <a
                              href={`mailto:${c.email}?subject=${encodeURIComponent("Happy Birthday!")}`}
                              title="Email"
                              className="rounded-md border border-[#2A2622] p-1.5 text-[#ADA79A] hover:border-[#B87F4B]/40 hover:text-[#D19A66]"
                            >
                              <Mail size={13} />
                            </a>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
};

export default BirthdayBell;
